import "./App.css";
import "bootstrap/dist/css/bootstrap.min.css";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import { useEffect, useState, useCallback } from "react";
import { createGlobalStyle } from "styled-components";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import toast, { Toaster } from "react-hot-toast";
import { onMessage } from "firebase/messaging";

import { useAuth } from "./AuthContext";
import { DropdownProvider } from "./Components/Header";
import { UserProvider } from "./UserContext";
import { generateToken, messaging } from "./firebase";
import instance from "./Api";
import AppToast from "./AppToast";

import Home from "./Components/Home";
import Header from "./Components/Header";
import HeaderLeft from "./Components/HeaderLeft";
import Footer from "./Components/Footer";
import SolutionsPage from "./Components/SolutionsPage";
import SolutionDetailPage from "./Components/SolutionDetailPage";
import Blog from "./Components/Blog";
import BlogsMore from "./Components/Details/BlogsMore";
import BlogSearchPage from "./Components/Details/BlogSearchPage";
import ProductPage from "./Components/ProductPage";
import ProductDetailsUbantu from "./Components/ProductDetailsUbantu";
import ProductDetailsWindows from "./Components/ProductDetailsWindows";
import PricingPage from "./Components/PricingPage";
import RestorePricingPage from "./Components/RestorePricingPage";
import TermsConditions from "./Components/TermsConditions";
import PrivacyPolicy from "./Components/PrivacyPolicy";
import Login from "./Components/User/Login";
import SignUp from "./Components/User/SignUp";
import ForgotPassword from "./Components/User/ForgotPassword";
import ResetPassword from "./Components/User/ResetPassword";
import ChangePassword from "./Components/User/ChangePassword";
import Profile from "./Components/User/Profile";
import InvoicePage from "./Components/User/InvoicePage";
import OTPVerify2FA from "./Components/User/OTPVerify2FA";
import ChildUserPage from "./Components/User/ChildUserPage";
import SettingsPage from "./Components/User/Settings";
import VMAssignPage from "./Components/User/VMAssignPage";
import AssignedMachinePage from "./Components/User/AssignedMachinePage";
import AssignedCDNPage from "./Components/User/AssignedCDNPage";
import UserSupportPage from "./Components/User/UserSupportPage";
import EmailVerification from "./Components/RegisteredUser/EmailVerification";
import BillingPage from "./Components/RegisteredUser/BillingPage";
import SupportPage from "./Components/RegisteredUser/SupportPage";
import MonitoringPage from "./Components/RegisteredUser/MonitoringPage";
import CDNPage from "./Components/RegisteredUser/CDNPage";
import CreateMachine from "./Components/RegisteredUser/CreateMachine";
import WalletPage from "./Components/RegisteredUser/WalletPage";
import MyMachinePage from "./Components/RegisteredUser/MyMachinePage";
import UtilisationsPage from "./Components/RegisteredUser/UtilisationsPage";
import Notification from "./Components/RegisteredUser/Notification";
import PaymentData from "./Components/RegisteredUser/PaymentData";
import BillReport from "./Components/RegisteredUser/BillReport";
import MachineStatus from "./Components/RegisteredUser/MachineStatus";
import EditMachine from "./Components/RegisteredUser/EditMachine";
import MonitorGraph from "./Components/RegisteredUser/MonitorGraph";
import VmResetPassword from "./Components/RegisteredUser/VmResetPassword";
import SsoLogin from "./Components/RegisteredUser/SsoLogin";
import StripePaymentPage from "./StripePaymentPage";
import AddonServicesPage from "./Components/RegisteredUser/AddonServicesPage";
import MyAddonsPage from "./Components/RegisteredUser/MyAddonsPage";

// ─── Fix 1: GlobalStyle MUST be defined at module level, NOT inside the component.
// Defining it inside causes "styled inside another component" warning on every render.
const GlobalStyle = createGlobalStyle`
  body {
    background-image: url('/main-bg.jpg');
    background-size: cover;
    background-position: center;
    background-repeat: round;
    height: 100vh;
    margin: 0;
    width: 100%;
  }
`;

const stripePromise = loadStripe(
  "pk_test_51OuUXdSJRv4SF2lDndvO2OGpb6PDwa9RM52p0ZtysACVET8x6di0EfKdvkA1j3nGMJEBjTFHrWbBuHoYeY038vU300tPGRTYei"
);

// ─── Pure helpers (outside component — no re-creation cost) ──────────────────
const isMobileDevice = () => window.matchMedia("(max-width: 1200px)").matches;

// ─── Main App Component ───────────────────────────────────────────────────────
const App = () => {
  const { smuser, isLoginByParentUser } = useAuth();

  const [popup, setPopup]     = useState(true);
  const [userIP, setUserIP]   = useState(null);
  const [isMobile, setIsMobile] = useState(isMobileDevice);

  // ─── FCM Token ──────────────────────────────────────────────────────────────
  const updateNotification = useCallback(async (token) => {
    try {
      await instance.post(
        `/saveUnauthUsersDeviceDetails?id=1&FCM_TOKEN=${token}&FINGERPRINT=${null}&DEVICE_LANGUAGE=${null}`
      );
    } catch (e) {
      console.error("Error saving FCM token:", e);
    }
  }, []);

  const sendTokenToServer = useCallback(async () => {
    const stored = localStorage.getItem("NewfcmUPNET");
    if (stored) {
      const token = stored.replace(/"/g, "");
      if (token) await updateNotification(token);
    } else {
      const token = await generateToken();
      if (token) await updateNotification(token);
    }
  }, [updateNotification]);

  // ─── IP fetch ───────────────────────────────────────────────────────────────
  const fetchUserIP = useCallback(async () => {
    try {
      const data = await window.fetchIPData();
      if (data?.country_name) {
        localStorage.setItem("current_country", data.country_name);
        setUserIP(data.ip || "unknown");
      } else {
        // Both APIs failed — still allow routes to render
        setUserIP("unknown");
      }
    } catch (e) {
      console.error("Error fetching user IP:", e);
      setUserIP("unknown"); // fallback so page never stays blank
    }
  }, []);

  useEffect(() => {
    sendTokenToServer();
    fetchUserIP();

    const unsubscribe = onMessage(messaging, (payload) => {
      const title = payload.notification?.title ?? payload.data?.title ?? "";
      const body  = payload.notification?.body  ?? payload.data?.body  ?? "";
      toast((t) => <AppToast id={t.id} message={`${title}\n${body}`} />);
    });

    const handleResize = () => setIsMobile(isMobileDevice());
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      if (typeof unsubscribe === "function") unsubscribe();
    };
  }, []); 

  // ─── Shared props ────────────────────────────────────────────────────────────
  const isParent = smuser !== null && isLoginByParentUser == 1;
  const isChild  = smuser !== null && isLoginByParentUser == 0;

  return (
    <Router>
      <DropdownProvider>
        <UserProvider>
          <GlobalStyle />
          <div
            style={{
              width: "100%",
              minHeight: isMobile ? "" : "75rem",
              position: "relative",
              backgroundImage: "url(./main-bg.jpg)",
              backgroundSize: "cover",
              backgroundRepeat: "round",
              backgroundBlendMode: "overlay",
            }}
            onClick={() => setPopup((p) => !p)}
          >
            <Header user={null} popup={popup} ip={userIP} />

            {userIP && (
              <Routes>
                {/* ── Public routes ───────────────────────────────────────── */}
                <Route path="/"                       element={<Home ip={userIP} />} />
                <Route path="/vm-reset/:token"        element={<VmResetPassword />} />
                <Route path="/solutions"              element={<SolutionsPage ip={userIP} />} />
                <Route path="/solutiondetail"         element={<SolutionDetailPage ip={userIP} />} />
                <Route path="/blog"                   element={<Blog ip={userIP} isMobile={isMobile} />} />
                <Route path="/blogmore"               element={<BlogsMore ip={userIP} />} />
                <Route path="/blogsearch"             element={<BlogSearchPage ip={userIP} />} />
                <Route path="/product"                element={<ProductPage isMobile={isMobile} />} />
                <Route path="/product-details-ubantu" element={<ProductDetailsUbantu isMobile={isMobile} ip={userIP} />} />
                <Route path="/product-details-windows"element={<ProductDetailsWindows isMobile={isMobile} />} />
                <Route path="/pricing"                element={<PricingPage isMobile={isMobile} />} />
                <Route path="/termsConditions"        element={<TermsConditions isMobile={isMobile} />} />
                <Route path="/privacy-policy"         element={<PrivacyPolicy isMobile={isMobile} />} />
                <Route path="/login"                  element={<Login isMobile={isMobile} ip={userIP} />} />
                <Route path="/sso"                    element={<SsoLogin />} />
                <Route path="/otpverify-2fa"          element={<OTPVerify2FA isMobile={isMobile} ip={userIP} />} />
                <Route path="/signUp"                 element={<SignUp isMobile={isMobile} ip={userIP} />} />
                <Route path="/forgotpassword"         element={<ForgotPassword isMobile={isMobile} />} />
                <Route path="/reset_password/*"       element={<ResetPassword isMobile={isMobile} />} />
                <Route path="/change-password"        element={<ChangePassword isMobile={isMobile} />} />
                <Route path="/email/verification"     element={<EmailVerification isMobile={isMobile} />} />

                {/* ── Auth-gated: CreateMachine & bill ────────────────────── */}
                <Route path="/vm/create" element={
                  isParent ? <CreateMachine isMobile={isMobile} /> :
                  isChild  ? <></> :
                             <SignUp isMobile={isMobile} />
                } />
                <Route path="/bill" element={
                  isParent ? <BillingPage isMobile={isMobile} /> :
                  isChild  ? <></> :
                             <SignUp isMobile={isMobile} />
                } />

                {/* ── Shared auth routes (parent + child) ─────────────────── */}
                {(isParent || isChild) && <>
                  <Route path="/create-ticket"      element={<SupportPage isMobile={isMobile} />} />
                  <Route path="/vm/monitor"         element={<MonitoringPage isMobile={isMobile} />} />
                  <Route path="/vm-machine"         element={<MyMachinePage isMobile={isMobile} />} />
                  <Route path="/vm/moniter/utilize" element={<UtilisationsPage isMobile={isMobile} />} />
                  <Route path="/vm/cdn"             element={<CDNPage isMobile={isMobile} />} />
                  <Route path="/edit-profile"       element={<Profile isMobile={isMobile} />} />
                  <Route path="/notification"       element={<Notification isMobile={isMobile} />} />
                  <Route path="/vm/status"          element={<MachineStatus isMobile={isMobile} />} />
                </>}

                {/* ── Parent-only routes ──────────────────────────────────── */}
                {isParent && <>
                  <Route path="/wallet"             element={<WalletPage isMobile={isMobile} />} />
                  <Route path="/settings"           element={<SettingsPage isMobile={isMobile} ip={userIP} />} />
                  <Route path="/childuser"          element={<ChildUserPage isMobile={isMobile} ip={userIP} />} />
                  <Route path="/vm-assign"          element={<VMAssignPage isMobile={isMobile} ip={userIP} />} />
                  <Route path="/assigned-vm"        element={<AssignedMachinePage isMobile={isMobile} ip={userIP} />} />
                  <Route path="/assigned-cdn"       element={<AssignedCDNPage isMobile={isMobile} ip={userIP} />} />
                  <Route path="/user-create-ticket" element={<UserSupportPage isMobile={isMobile} ip={userIP} />} />
                  <Route path="/restore-pricing"    element={<RestorePricingPage isMobile={isMobile} ip={userIP} />} />
                  <Route path="/paymentdata"        element={<PaymentData isMobile={isMobile} />} />
                  <Route path="/billreport"         element={<BillReport isMobile={isMobile} />} />
                  <Route path="/invoice"            element={<InvoicePage isMobile={isMobile} />} />
                  <Route path="/edit-vm"            element={<EditMachine isMobile={isMobile} />} />
                  <Route path="/succes-payment"     element={<EditMachine isMobile={isMobile} />} />
                  <Route path="/stripe-paymentpage" element={<StripePaymentPage isMobile={isMobile} />} />
                  <Route path="/monitor-graph"      element={<MonitorGraph isMobile={isMobile} />} />
                  <Route path="/addon-services" element={<AddonServicesPage isMobile={isMobile} />} />
                  <Route path="/my-addons"      element={<MyAddonsPage      isMobile={isMobile} />} />
                </>}

              </Routes>
            )}

            <Footer ip={userIP} />
          </div>
          <HeaderLeft />
        </UserProvider>
      </DropdownProvider>
    </Router>
  );
};

export default App;